import PropTypes from 'prop-types';
import { useState } from 'react';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';
import { handleUserRefetch } from '../helpers/sbCreatorsRootUserRefetch';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';

/* Components */
import { SbRespCreatorsComponents as SbRespCreatorsRootComponents } from '../SbRespCreatorsComponents/SbRespCreatorsComponents';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead';
import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';

/* Hooks */
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useSharedUserAndRelayVarsLoader } from '../../shared/SharedUserAndRelayVarLoader';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage';
import { useSbSharedPageTracking } from '../hooks/useSbSharedPageTracking';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';

const SbRespCreatorsLayoutComponent = props => {
    const [isClient, setIsClient] = useState(false);
    const { isTablet } = useServerVarsContext();
    const { viewer, reinitTracking, relay } = props;
    const user = viewer.user || null;
    const { itemSearch } = viewer;
    useSharedUserAndRelayVarsLoader({ onUserRefetch: handleUserRefetch(relay, setIsClient) });

    useDwelledOnPage(PAGE_TYPE.SEARCH);
    useSbSharedPageTracking({
        viewer,
        itemSearch,
        user,
        reinitTracking,
        refineMenuFiltersMap: sbRespRefineMenuFiltersMap,
    });

    return (
        <RecentlyViewedItemsProvider maxItems={isTablet ? 10 : 12} itemsPerPage={isTablet ? 5 : 6}>
            <SbSharedHead itemSearch={itemSearch} />
            <SbRespCreatorsRootComponents
                viewer={viewer}
                itemSearch={itemSearch}
                user={user}
                isClient={isClient}
            />
        </RecentlyViewedItemsProvider>
    );
};

SbRespCreatorsLayoutComponent.propTypes = {
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    localeDomain: PropTypes.string.isRequired,
    reinitTracking: PropTypes.func,
};

const mapStateToProps = state => {
    const { intlVariables, relayVariables } = state;

    return {
        localeDomain: intlVariables.localeDomain,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespCreatorsLayout = createRefetchContainer(
    connect(mapStateToProps)(SbRespCreatorsLayoutComponent),
    {
        viewer: graphql`
            fragment SbRespCreatorsLayout_viewer on Viewer {
                ...SbRespCreatorsComponents_viewer
                ...useSbSharedPageTracking_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    ...useSbSharedPageTracking_user
                    ...SbRespCreatorsComponents_user
                }
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    regionsList: $regionsList
                    priceBookName: $priceBookName
                ) {
                    ...SbSharedHead_itemSearch
                    ...SbRespCreatorsComponents_itemSearch
                    ...SbSharedRefetchContainer_itemSearch
                    ...useSbSharedPageTracking_itemSearch
                }
            }
        `,
    },
    graphql`
        query SbRespCreatorsLayoutRefetchQuery(
            $userId: String = ""
            $fetchUser: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $rerankUserId: String = ""
            $rerankGuestId: String = ""
            $isClient: Boolean!
        ) {
            viewer {
                ...useSbSharedPageTracking_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    ...useSbSharedPageTracking_user
                    ...SbRespCreatorsComponents_user
                }
            }
        }
    `
);
